export interface FinancialTerm {
  key: string;
  label: string;
  aliases: string[];
  definition: string;
}

export const FINANCIAL_TERMS: FinancialTerm[] = [
  { key: "apr", label: "APR", aliases: ["APR"], definition: "APR means Annual Percentage Rate. It's the yearly cost of borrowing money on a loan or credit card." },
  { key: "roi", label: "ROI", aliases: ["ROI"], definition: "ROI means Return on Investment. It tells you how much money you gained or lost compared with what you put in." },
  { key: "rd", label: "R&D", aliases: ["R&D", "R and D"], definition: "R&D means research and development. It's money a company spends to create or improve products." },
  { key: "etf", label: "ETF", aliases: ["ETF", "ETFs"], definition: "An ETF is a bundle of investments you can buy all at once, like a basket of stocks." },
  { key: "net-worth", label: "Net worth", aliases: ["net worth"], definition: "Net worth is what you own minus what you owe." },
  { key: "assets", label: "Assets", aliases: ["assets", "asset"], definition: "Assets are things you own that have value, like cash, a car, or investments." },
  { key: "liabilities", label: "Liabilities", aliases: ["liabilities", "liability"], definition: "Liabilities are debts or bills you still owe, like a loan or credit card balance." },
  { key: "cash-flow", label: "Cash flow", aliases: ["cash flow"], definition: "Cash flow is the money coming in and going out over time." },
  { key: "margin", label: "Margin", aliases: ["margin", "margins", "profit margin"], definition: "Margin is the part of your sales money left after costs. Profit margin shows how much profit you keep from each dollar earned." },
  { key: "revenue", label: "Revenue", aliases: ["revenue"], definition: "Revenue is the total money a business brings in before subtracting costs." },
  { key: "equity", label: "Equity", aliases: ["equity"], definition: "Equity is the part of something you truly own after subtracting what you still owe on it." },
  { key: "debt", label: "Debt", aliases: ["debt"], definition: "Debt is money you borrowed and need to pay back." },
  { key: "interest", label: "Interest", aliases: ["interest"], definition: "Interest is the extra money charged for borrowing money, or earned for saving or investing it." },
  { key: "investing", label: "Investing", aliases: ["investing", "investment"], definition: "Investing means putting money into something now with the goal of growing it later." },
  { key: "profit", label: "Profit", aliases: ["profit", "profits"], definition: "Profit is the money left after a business pays all of its costs." },
  { key: "expenses", label: "Expenses", aliases: ["expenses", "expense"], definition: "Expenses are the costs a person or business pays to operate, like rent, supplies, or bills." },
  { key: "overhead", label: "Overhead", aliases: ["overhead"], definition: "Overhead is the ongoing cost of running a business, even when you are not making a sale." },
  { key: "inventory", label: "Inventory", aliases: ["inventory"], definition: "Inventory is the products or materials a business keeps on hand to sell or use." },
  { key: "break-even", label: "Break-even point", aliases: ["break-even", "break even", "break-even point", "break even point"], definition: "The break-even point is when money earned exactly matches money spent, so there is no profit or loss." },
  { key: "startup-capital", label: "Startup capital", aliases: ["startup capital", "starting capital", "seed money"], definition: "Startup capital is the money needed to launch a business and cover early costs." },
  { key: "payroll", label: "Payroll", aliases: ["payroll"], definition: "Payroll is the total money a business pays its employees for their work." },
  { key: "principal", label: "Principal", aliases: ["principal"], definition: "Principal is the original amount of money you borrow or invest before interest is added." },
  { key: "credit-utilization", label: "Credit utilization", aliases: ["credit utilization"], definition: "Credit utilization is how much of your available credit you are using." },
  { key: "diversification", label: "Diversification", aliases: ["diversification", "diversify"], definition: "Diversification means spreading money across different investments to lower risk." },
  { key: "customer-acquisition-cost", label: "Customer acquisition cost", aliases: ["customer acquisition cost", "CAC"], definition: "Customer acquisition cost is how much a business spends to gain one new customer." },
  { key: "operating-costs", label: "Operating costs", aliases: ["operating costs", "operating cost"], definition: "Operating costs are the day-to-day costs required to keep a business running." },
];

const escapeRegex = (value: string) => value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

export function extractFinancialTerms(texts: string[]): FinancialTerm[] {
  const haystack = texts.join(" ").toLowerCase();
  return FINANCIAL_TERMS.filter((term) => term.aliases.some((alias) => haystack.includes(alias.toLowerCase())));
}

export function findTermByLabel(label: string | null | undefined) {
  if (!label) return null;
  const normalized = label.trim().toLowerCase();
  return FINANCIAL_TERMS.find((term) => term.label.toLowerCase() === normalized || term.aliases.some((alias) => alias.toLowerCase() === normalized)) || null;
}

export function splitTextByFinancialTerms(text: string) {
  if (!text) return [{ type: "text" as const, value: "" }];

  const matches: Array<{ start: number; end: number; label: string }> = [];

  FINANCIAL_TERMS.forEach((term) => {
    term.aliases.forEach((alias) => {
      const regex = new RegExp(`\\b${escapeRegex(alias)}\\b`, "gi");
      let match: RegExpExecArray | null;
      while ((match = regex.exec(text)) !== null) {
        matches.push({
          start: match.index,
          end: match.index + match[0].length,
          label: term.label,
        });
      }
    });
  });

  const ordered = matches.sort((a, b) => a.start - b.start || b.end - a.end);
  const filtered: typeof ordered = [];

  ordered.forEach((match) => {
    const overlaps = filtered.some((existing) => !(match.end <= existing.start || match.start >= existing.end));
    if (!overlaps) filtered.push(match);
  });

  if (filtered.length === 0) return [{ type: "text" as const, value: text }];

  const parts: Array<{ type: "text" | "term"; value: string; label?: string }> = [];
  let cursor = 0;

  filtered.forEach((match) => {
    if (match.start > cursor) {
      parts.push({ type: "text", value: text.slice(cursor, match.start) });
    }
    parts.push({ type: "term", value: text.slice(match.start, match.end), label: match.label });
    cursor = match.end;
  });

  if (cursor < text.length) {
    parts.push({ type: "text", value: text.slice(cursor) });
  }

  return parts;
}
